import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, CalendarClock } from "lucide-react";
import { FadeUp } from "@/components/motion";
import { track } from "@/lib/analytics";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_URL = process.env.REACT_APP_BOOKING_URL;

const SERVICES = [
  "Career Strategy, CV and LinkedIn",
  "Small Business and Self Employment",
  "I am unsure and would like guidance",
];
const CONTACT_METHODS = ["Email", "Phone", "LinkedIn message"];

export default function Consultation() {
  const [form, setForm] = useState(() => ({
    full_name: "", email: "", current_role: "", target_role: "",
    service_interest: sessionStorage.getItem("enquiry_interest") || "", contact_method: "", notes: "",
  }));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const onInterest = (e) => setForm((f) => ({ ...f, service_interest: e.detail }));
    window.addEventListener("enquiry-interest", onInterest);
    return () => window.removeEventListener("enquiry-interest", onInterest);
  }, []);

  const validate = () => {
    const err = {};
    if (!form.full_name.trim()) err.full_name = "Please enter your full name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) err.email = "Please enter a valid email address.";
    if (!form.service_interest) err.service_interest = "Please choose an option.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading || done) return;
    if (!validate()) return;
    setLoading(true);
    track("consultation_submitted", { service: form.service_interest });
    try {
      await axios.post(`${API}/consultation`, form);
      setDone(true);
      toast.success("Request received — George will be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="consultation" id="consultation" className="relative bg-[#0A0A0A] py-28 md:py-40 scroll-mt-28">
      <span id="lead" className="absolute -top-24" aria-hidden="true" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16">
        <FadeUp>
          <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-6">Enquiry</p>
          <h2 className="font-display font-bold tracking-tighter leading-[0.95] text-4xl md:text-6xl text-[#F8F8F5]">
            Tell us what you would like <span className="font-serif-accent font-light">help with.</span>
          </h2>
          <p className="mt-6 font-body text-[18px] text-[#D4D4D8] leading-[1.6] max-w-md">
            A friendly conversation with George about your skills, your goals and your next step.
            We will explain the scope and give you a clear quote before you decide anything.
          </p>
          <p data-testid="enquiry-no-commitment" className="mt-4 font-body text-[16px] text-[#A1A1AA] leading-[1.6] max-w-md">
            Sending an enquiry does not commit you to purchasing a service.
          </p>
          <p className="mt-5 font-body text-sm text-[#A1A1AA] leading-relaxed max-w-md">
            Read our{" "}
            <a
              data-testid="consultation-client-protection-link"
              href={`${API}/client-protection/pack`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#DFFF00] hover:underline underline-offset-4 font-semibold"
            >
              Client Protection Pack
            </a>
            , including our service terms, refunds, complaints and privacy information.
          </p>
          {BOOKING_URL ? (
            <a
              href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
              data-testid="booking-link"
              onClick={() => track("consultation_started", { from: "booking_link" })}
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#27272A] text-[#F8F8F5] font-body px-6 py-3 hover:border-[#DFFF00] transition-colors"
            >
              <CalendarClock size={17} /> Prefer to pick a time now? Open the calendar
            </a>
          ) : null}
        </FadeUp>

        <FadeUp delay={0.1}>
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="success" data-testid="consultation-success"
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-start justify-center border border-[#27272A] p-10"
              >
                <div className="w-14 h-14 rounded-full bg-[#DFFF00] flex items-center justify-center">
                  <Check className="text-[#0A0A0A]" size={26} />
                </div>
                <h3 className="font-display text-3xl font-bold text-[#F8F8F5] mt-6">Request received.</h3>
                <p className="font-body text-[#A1A1AA] mt-3 leading-relaxed">
                  Thanks {form.full_name.split(" ")[0]} — George will personally reply within one business day
                  at {form.email}. Remember, your enquiry does not commit you to anything.
                </p>
              </motion.div>
            ) : (
              <motion.form key="form" data-testid="consultation-form" onSubmit={submit} noValidate className="space-y-6" exit={{ opacity: 0 }}>
                <Field id="c-name" label="Full name" error={errors.full_name} testid="consult-name" value={form.full_name} onChange={set("full_name")} placeholder="Jane Doe" />
                <Field id="c-email" label="Email" type="email" error={errors.email} testid="consult-email" value={form.email} onChange={set("email")} placeholder="jane@email.com" />
                <Field id="c-role" label="Current role (optional)" testid="consult-role" value={form.current_role} onChange={set("current_role")} placeholder="e.g. Registered Nurse" />
                <Field id="c-target" label="Target role or career goal (optional)" testid="consult-target" value={form.target_role} onChange={set("target_role")} placeholder="e.g. Team Leader, career change" />

                <div>
                  <label className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B] block mb-2">What would you like help with?</label>
                  <Select value={form.service_interest} onValueChange={(v) => setForm((f) => ({ ...f, service_interest: v }))}>
                    <SelectTrigger data-testid="consult-service" className="w-full bg-transparent border-0 border-b border-[#27272A] rounded-none px-0.5 py-3 h-auto text-[#F8F8F5] focus:ring-0 focus:border-[#DFFF00]">
                      <SelectValue placeholder="Choose an option" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#27272A] text-[#F8F8F5]">
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} className="focus:bg-[#DFFF00] focus:text-[#0A0A0A]">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.service_interest && <p role="alert" className="mt-1 text-xs text-red-400">{errors.service_interest}</p>}
                </div>

                <div>
                  <label className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B] block mb-2">Preferred contact method (optional)</label>
                  <Select value={form.contact_method} onValueChange={(v) => setForm((f) => ({ ...f, contact_method: v }))}>
                    <SelectTrigger data-testid="consult-contact" className="w-full bg-transparent border-0 border-b border-[#27272A] rounded-none px-0.5 py-3 h-auto text-[#F8F8F5] focus:ring-0 focus:border-[#DFFF00]">
                      <SelectValue placeholder="How should George reach you?" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#27272A] text-[#F8F8F5]">
                      {CONTACT_METHODS.map((s) => (
                        <SelectItem key={s} value={s} className="focus:bg-[#DFFF00] focus:text-[#0A0A0A]">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Field id="c-notes" label="Briefly, what do you want help with? (optional)" testid="consult-notes" value={form.notes} onChange={set("notes")} placeholder="e.g. new CV, career change, business idea, interview practice…" />

                <button
                  data-testid="consult-submit" type="submit" disabled={loading}
                  className="w-full rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 flex items-center justify-center gap-2 hover:bg-[#C8E600] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                  {loading ? "Sending…" : "Send your enquiry"}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </FadeUp>
      </div>
    </section>
  );
}

function Field({ id, label, type = "text", error, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">{label}</label>
      <input
        id={id} type={type} data-testid={testid} value={value} onChange={onChange} placeholder={placeholder}
        aria-invalid={!!error} className="brutalist-input"
      />
      {error && <p role="alert" className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}
